import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th, EmptyRow } from '@/components/erp/primitives'
import { RankedBars } from '@/shared/ui/charts'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { FilterBar } from '@/shared/ui/filters'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import { coverageGeography, coverageMarkSummary, coverageSummary, coverageWatchers } from './api'
import { fmtBytes } from './players'
import { markKindLabel } from './vocab'
import { DatePicker } from '@/components/erp/date-picker'

const UNIT_LEVELS = [
  { code: 'COUNTY', label: 'County' },
  { code: 'CONSTITUENCY', label: 'Constituency' },
  { code: 'WARD', label: 'Ward' },
] as const

const pct = (v: string | number | null | undefined) =>
  v == null ? '—' : `${(Number(v) * 100).toFixed(1)}%`

const hours = (minutes: number) =>
  minutes >= 120 ? `${(minutes / 60).toFixed(1)} h` : `${fmtNumber(minutes)} min`

export function CoverageAnalyticsPage() {
  const canRead = useCan('coverage:read')
  const [params, setParams] = useSearchParams()
  const [unitType, setUnitType] = useState('COUNTY')

  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  const set = (k: string, v: string) => {
    const sp = new URLSearchParams(params)
    if (v) sp.set(k, v); else sp.delete(k)
    setParams(sp, { replace: true })
  }
  /**
   * `to` is the END of the chosen day, in the reader's own zone.
   * `new Date('2026-08-22')` is UTC midnight at the START of the 22nd, so a range
   * of 1–22 August silently drops everything that happened on the 22nd and a
   * same-day range returns an empty window that reads as "nothing happened".
   */
  const range = {
    from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
  }

  const summary = useLoad(() => coverageSummary(range), [from, to])
  const geography = useLoad(() => coverageGeography({ ...range, unitTypeCode: unitType }), [from, to, unitType])
  const marks = useLoad(() => coverageMarkSummary(range), [from, to])

  if (!canRead) return <Forbidden what="live coverage" />

  const s = summary.data

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Coverage Analytics"
        description="Did somebody film it, and did anybody watch."
      />

      <FilterBar>
        <label className="flex items-center gap-1.5 text-erp-xs">
          From
          <DatePicker value={from} onChange={(v) => set('from', v)} className="h-9" />
        </label>
        <label className="flex items-center gap-1.5 text-erp-xs">
          To
          <DatePicker value={to} onChange={(v) => set('to', v)} className="h-9" />
        </label>
        {(from || to) && (
          <button type="button" className="erp-btn-ghost h-9 text-erp-sm text-chocolate"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            Clear
          </button>
        )}
      </FilterBar>

      <ErrorStrip message={summary.error} />

      {summary.loading && !s ? <LoadingBlock label="Counting…" /> : s && (
        <>
          <KpiStrip>
            <MetricCard label="Events" value={fmtNumber(s.events)} sub="in this window" />
            <MetricCard
              label="Covered"
              value={fmtNumber(s.eventsCovered)}
              sub={`${pct(s.coverageRate)} of the diary`}
            />
            {/* The number nobody currently has, and the reason this screen exists. */}
            {/*
              With ZERO events in range, `eventsUncovered` is 0 and "every event
              was filmed" would be a statement about an empty set. Say what is
              actually true instead.
            */}
            <MetricCard
              label="Nobody filmed"
              value={fmtNumber(s.eventsUncovered)}
              sub={s.events === 0 ? 'no events in this window'
                : s.eventsUncovered > 0 ? 'no camera ever started'
                : 'every event was filmed'}
              accent={s.eventsUncovered > 0}
            />
            <MetricCard label="On air" value={hours(s.liveMinutes)} sub={`${fmtNumber(s.feedsThatWentLive)} cameras`} />
            <MetricCard label="Archive" value={fmtNumber(s.recordings)} sub={fmtBytes(s.recordingBytes)} />
          </KpiStrip>

          <Panel title="Watching, in total" description="Live watching is measured by the media server, not reported by the browser.">
            <dl className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
              <Stat label="People who watched" value={fmtNumber(s.distinctWatchers)} />
              <Stat label="Live watch time" value={hours(s.liveWatchMinutes)} />
              <Stat label="Recordings opened" value={fmtNumber(s.replayOpens)} />
              {/*
                `replayMinutes` is null and is rendered as a refusal rather than a
                zero. MinIO serves ranged reads and does not report what was
                watched, so replay watch time is genuinely unknown — and "0 min"
                beside "148 opened" would state that nobody watched any of it.
              */}
              <Stat
                label="Replay watch time"
                value={s.replayMinutes == null ? 'Not measurable' : hours(s.replayMinutes)}
                muted={s.replayMinutes == null}
              />
            </dl>
            <div className="mt-3">
              <Note tone="info">
                Live watch time counts only sessions that have finished. Somebody watching
                right now contributes nothing until they close the feed — a figure that grows
                while you look at it is worse than a settled one.
              </Note>
            </div>
          </Panel>
        </>
      )}

      <Panel
        title="Where we are not filming"
        description="Units with no events are listed with zeros — that is the answer, not missing data."
        right={(
          <select className="erp-input h-8" value={unitType} aria-label="Unit level"
            onChange={(e) => setUnitType(e.target.value)}>
            {UNIT_LEVELS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        )}
      >
        <ErrorStrip message={geography.error} />
        {geography.loading && !geography.data ? <LoadingBlock label="Loading geography…" /> : (
          <>
            <RankedBars
              points={(geography.data?.rows ?? [])
                .filter((r) => r.uncovered > 0)
                .sort((a, b) => b.uncovered - a.uncovered)
                .slice(0, 12)
                .map((r) => ({
                  label: r.unitName,
                  value: r.uncovered,
                  hint: `${fmtNumber(r.uncovered)} of ${fmtNumber(r.events)} events had no camera`,
                }))}
              emptyMessage={s && s.events === 0
                ? 'No events in this window.'
                : 'Every event in this window was filmed.'}
            />
            <div className="mt-3 overflow-x-auto">
              <ErpTable head={(
                <>
                  <Th>Unit</Th>
                  <Th className="text-right">Events</Th>
                  <Th className="text-right">Filmed</Th>
                  <Th className="text-right">Not filmed</Th>
                  <Th className="text-right hidden sm:table-cell">Rate</Th>
                  <Th className="text-right hidden md:table-cell">On air</Th>
                </>
              )}>
                {(geography.data?.rows ?? []).length === 0
                  ? <EmptyRow colSpan={6} message="No units in scope." />
                  : (geography.data?.rows ?? []).map((r) => (
                    <tr key={r.unitId}>
                      <Td>{r.unitName}</Td>
                      <Td className="text-right tabular-nums">{fmtNumber(r.events)}</Td>
                      <Td className="text-right tabular-nums">{fmtNumber(r.covered)}</Td>
                      <Td className={`text-right tabular-nums ${r.uncovered > 0 ? 'text-amber-700 dark:text-amber-400' : ''}`}>
                        {fmtNumber(r.uncovered)}
                      </Td>
                      <Td className="text-right tabular-nums hidden sm:table-cell">{pct(r.coverageRate)}</Td>
                      <Td className="text-right tabular-nums hidden md:table-cell">{hours(r.liveMinutes)}</Td>
                    </tr>
                  ))}
              </ErpTable>
            </div>
            {geography.data?.truncated && (
              <div className="mt-2">
                <Note tone="info">
                  Showing the first {fmtNumber(geography.data.cap)} units. Narrow the level or
                  the date range to see the rest — the list is cut, not summarised, so the
                  totals above still count everything.
                </Note>
              </div>
            )}
          </>
        )}
      </Panel>

      <Panel title="Where something happened" description="Marks per event — the cheap proxy for whether a recording is worth opening.">
        <ErrorStrip message={marks.error} />
        {marks.loading && !marks.data ? <LoadingBlock label="Loading moments…" />
          : (marks.data ?? []).length === 0 ? (
            <Note tone="info">Nothing has been marked in this window.</Note>
          ) : (
            <ul className="space-y-1">
              {(marks.data ?? []).slice(0, 20).map((m) => (
                <li key={m.eventId} className="flex flex-wrap items-baseline gap-2 text-erp-sm">
                  <span className="font-mono text-erp-xs text-muted-foreground">{m.ref}</span>
                  <span className="truncate font-medium">{m.title}</span>
                  <span className="tabular-nums">{fmtNumber(m.marks)}</span>
                  <span className="text-erp-xs text-muted-foreground">
                    {Object.entries(m.byKind).map(([k, n]) => `${markKindLabel(k)} ${n}`).join(' · ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
      </Panel>

      <WatchersPanel from={range.from} to={range.to} />
    </div>
  )
}

/**
 * A report on individual staff viewing behaviour.
 *
 * It is legitimate — it answers whether senior people actually watched the ground
 * — and it is exactly the sort of report that gets repurposed, so it is behind a
 * deliberate click rather than rendered on arrival, and it says what it is. Every
 * call is audited as a security event server-side.
 */
function WatchersPanel({ from, to }: { from?: string; to?: string }) {
  const [open, setOpen] = useState(false)
  const { data, error, loading } = useLoad(
    () => (open ? coverageWatchers({ from, to, limit: 100 }) : Promise.resolve(null)),
    [open, from, to],
  )

  return (
    <Panel
      title="Who watched"
      description="Named individuals and how long they watched."
      right={!open ? (
        <button type="button" className="erp-btn h-8" onClick={() => setOpen(true)}>Show</button>
      ) : undefined}
    >
      {!open ? (
        <Note tone="info">
          This lists named people and their viewing time. It is recorded in the audit log as
          a security event each time it is opened, because a report on people is itself an
          act on people.
        </Note>
      ) : (
        <>
          <ErrorStrip message={error} />
          {loading && !data ? <LoadingBlock label="Loading…" /> : (
            <ErpTable head={(
              <>
                <Th>Person</Th>
                <Th className="text-right">Live watch time</Th>
                <Th className="text-right">Recordings opened</Th>
                <Th className="hidden sm:table-cell">Last watched</Th>
              </>
            )}>
              {(data ?? []).length === 0
                ? <EmptyRow colSpan={4} message="Nobody watched anything in this window." />
                : (data ?? []).map((w) => (
                  <tr key={w.userId}>
                    <Td>{w.name ?? w.userId}</Td>
                    <Td className="text-right tabular-nums">{hours(w.liveMinutes)}</Td>
                    <Td className="text-right tabular-nums">{fmtNumber(w.replayOpens)}</Td>
                    <Td className="hidden sm:table-cell">
                      {w.lastWatchedAt ? new Date(w.lastWatchedAt).toLocaleString() : '—'}
                    </Td>
                  </tr>
                ))}
            </ErpTable>
          )}
        </>
      )}
    </Panel>
  )
}

function Stat({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div>
      <dt className="text-erp-xs text-muted-foreground">{label}</dt>
      <dd className={muted ? 'text-erp-md text-muted-foreground' : 'text-erp-lg font-semibold tabular-nums'}>
        {value}
      </dd>
    </div>
  )
}
